import {
  Box,
  Button,
  Flex,
  FormLabel,
  Heading,
  Image,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { setEssayAnswersRequest, getEssayAnswersRequest } from "../../../modules/admin-modules/exams/Actions";

const QUESTION_BASE_URL = "https://asquera.com/asquera";
const PDF_BASE_URL = "https://api.asquera.com/storage/app/student_pdfs";
const googlePdfPreviewUrl = (url) =>
  `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(url)}`;

const CorrectStudentModal = ({ isOpen, onClose, studentGroup, examId }) => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.exams.isLoading);
  const [marks, setMarks] = useState({});

  useEffect(() => {
    if (!studentGroup?.answers) {
      setMarks({});
      return;
    }
    const next = {};
    studentGroup.answers.forEach((answer) => {
      next[answer.id] = {
        mark: answer.mark ?? "",
        wrong: String(answer.wrong ?? 0),
      };
    });
    setMarks(next);
  }, [studentGroup]);

  const isCorrected =
    studentGroup?.status === "corrected" ||
    (studentGroup?.answers?.length > 0 &&
      studentGroup.answers.every(
        (answer) => answer?.mark != null && answer?.mark !== ""
      ));
  const studentPdf = studentGroup?.student_exam?.student_pdf;
  const pdfUrl = studentPdf ? `${PDF_BASE_URL}/${studentPdf}` : null;

  const updateMark = (answerId, patch) => {
    setMarks((prev) => ({
      ...prev,
      [answerId]: { ...prev[answerId], ...patch },
    }));
  };

  const handleSave = () => {
    const answers = (studentGroup?.answers || []).map((answer) => ({
      id: answer.id,
      mark: Number(marks[answer.id]?.mark ?? 0),
      wrong: Number(marks[answer.id]?.wrong ?? 0),
    }));
    if (!studentGroup?.student_exam?.id) {
      toast.error("student exam not found");
      return;
    }
    if (answers.some((a) => a.mark === "" || Number.isNaN(a.mark))) {
      toast.error("أدخل درجة لكل سؤال");
      return;
    }
    dispatch(
      setEssayAnswersRequest(
        {
          student_exam_id: studentGroup.student_exam.id,
          answers,
        },
        () => {
          onClose();
          if (examId) {
            dispatch(getEssayAnswersRequest(examId));
          }
        }
      )
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
      <ModalOverlay />
      <ModalContent maxW="95vw" h="90vh">
        <ModalHeader color="main_1">
          تصحيح إجابات {studentGroup?.name}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody overflow="hidden" pb={2}>
          <Flex h="100%" gap={4} dir="ltr">
            <Box
              w="50%"
              h="100%"
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="12px"
              overflow="hidden"
              bg="gray.50"
            >
              {pdfUrl ? (
                <Flex direction="column" h="100%">
                  <Flex
                    justify="space-between"
                    align="center"
                    px={3}
                    py={2}
                    bg="white"
                    borderBottomWidth="1px"
                  >
                    <Text fontWeight="600" fontSize="14px">
                      ملف الطالب PDF
                    </Text>
                    <Link href={pdfUrl} isExternal color="main_1" fontSize="13px">
                      فتح في تبويب جديد
                    </Link>
                  </Flex>
                  <Box flex={1}>
                    <iframe
                      title="student-pdf"
                      src={googlePdfPreviewUrl(pdfUrl)}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                    />
                  </Box>
                </Flex>
              ) : (
                <Box p={4} h="100%" overflowY="auto" dir="rtl">
                  <Text fontWeight="700" color="main_1" mb={3}>
                    إجابات الطالب النصية
                  </Text>
                  <Stack spacing={4}>
                    {studentGroup?.answers?.map((answer, idx) => (
                      <Box
                        key={answer.id}
                        p={4}
                        bg="white"
                        borderRadius="12px"
                        borderWidth="1px"
                        borderColor="gray.200"
                      >
                        <Text fontWeight="600" mb={2}>
                          سؤال {idx + 1}
                        </Text>
                        <Box
                          fontSize="14px"
                          mb={3}
                          dangerouslySetInnerHTML={{
                            __html: answer?.question?.question || "",
                          }}
                        />
                        <Textarea
                          value={answer?.essay_answer || "لا توجد إجابة نصية"}
                          isReadOnly
                          minH="100px"
                          bg="#fafafa"
                        />
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>

            <Box
              w="50%"
              h="100%"
              overflowY="auto"
              p={4}
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="12px"
              dir="rtl"
            >
              <Heading size="sm" color="main_1" mb={4}>
                رصد الدرجات
              </Heading>
              {isCorrected && (
                <Text fontSize="14px" color="green.600" mb={3}>
                  تم التصحيح — لا يمكن تعديل الدرجات
                </Text>
              )}
              <Stack spacing={4}>
                {studentGroup?.answers?.map((answer, idx) => {
                  const maxMark = Number(answer?.question?.mark) || 1;
                  const values = marks[answer.id] || { mark: "", wrong: "0" };
                  return (
                    <Box
                      key={answer.id}
                      p={4}
                      bg="#fafafa"
                      borderRadius="12px"
                      borderWidth="1px"
                      borderColor="gray.200"
                    >
                      <Text fontWeight="700" mb={2}>
                        سؤال {idx + 1}
                      </Text>
                      <Box
                        fontSize="14px"
                        mb={3}
                        sx={{ "& p": { margin: 0 } }}
                        dangerouslySetInnerHTML={{
                          __html: answer?.question?.question || "",
                        }}
                      />
                      {answer?.question?.image && (
                        <Image
                          src={`${QUESTION_BASE_URL}/${answer.question.image}`}
                          alt={`question-${idx + 1}`}
                          maxH="160px"
                          objectFit="contain"
                          mb={3}
                          borderRadius="8px"
                        />
                      )}
                      <Text fontSize="13px" color="gray.600" mb={2}>
                        الدرجة الكاملة: {maxMark}
                      </Text>
                      <FormLabel fontSize="14px" mb={1}>
                        الدرجة
                      </FormLabel>
                      <Input
                        type="number"
                        min={0}
                        max={maxMark}
                        value={values.mark}
                        isReadOnly={isCorrected}
                        isDisabled={isCorrected}
                        onChange={(e) =>
                          updateMark(answer.id, { mark: e.target.value })
                        }
                        bg="white"
                        borderRadius="12px"
                        mb={3}
                      />
                      <FormLabel fontSize="14px" mb={1}>
                        الحالة
                      </FormLabel>
                      <Stack direction="row" spacing={5}>
                        <label>
                          <input
                            type="radio"
                            name={`wrong-${answer.id}`}
                            value="0"
                            checked={values.wrong === "0"}
                            disabled={isCorrected}
                            onChange={() => updateMark(answer.id, { wrong: "0" })}
                          />{" "}
                          صحيح
                        </label>
                        <label>
                          <input
                            type="radio"
                            name={`wrong-${answer.id}`}
                            value="1"
                            checked={values.wrong === "1"}
                            disabled={isCorrected}
                            onChange={() => updateMark(answer.id, { wrong: "1" })}
                          />{" "}
                          خطأ
                        </label>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" ml={3} onClick={onClose}>
            إغلاق
          </Button>
          {!isCorrected && (
            <Button
              bg="main_1"
              color="white"
              isLoading={isLoading}
              onClick={handleSave}
            >
              حفظ الدرجات
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CorrectStudentModal;
